import { useEffect, useRef, useState } from 'react'

import { NAVIGATION_COPY } from '@/locales/en'
import { useUpdateTradeConfirmationEmail } from '@/account/accountQueries'
import { AccountSettingsFormLayout, AccountSettingsSuccessLayout } from './AccountSettingsFormLayout'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Props = {
    accountId: string
    initialEmail?: string
    onSave: () => void
    onCancel: () => void
}

export default function TradeConfirmationEmailView({ accountId, initialEmail = '', onSave, onCancel }: Props) {
    const copy = NAVIGATION_COPY.account.tradeConfirmationForm
    const successCopy = NAVIGATION_COPY.account.tradeConfirmationSuccess
    const [email, setEmail] = useState(initialEmail)
    const [savedEmail, setSavedEmail] = useState<string | null>(null)
    const updateTradeConfirmationEmail = useUpdateTradeConfirmationEmail()
    const hasSyncedInitialEmail = useRef(Boolean(initialEmail))
    const hasEditedEmail = useRef(false)

    useEffect(() => {
        if (!hasSyncedInitialEmail.current && !hasEditedEmail.current && initialEmail) {
            hasSyncedInitialEmail.current = true
            setEmail(initialEmail)
        }
    }, [initialEmail])

    const isValid = EMAIL_REGEX.test(email.trim())
    const isLoading = updateTradeConfirmationEmail.isPending

    const handleSave = async () => {
        if (isValid) {
            try {
                await updateTradeConfirmationEmail.mutateAsync({
                    accountId,
                    email: email.trim(),
                })
                setSavedEmail(email.trim())
            } catch {
                // Error toast is handled by the mutation hook
            }
        }
    }

    if (savedEmail) {
        return (
            <AccountSettingsSuccessLayout
                title={successCopy.pageTitle}
                onClose={onSave}
                closeLabel={successCopy.backToAccount}
            />
        )
    }

    return (
        <AccountSettingsFormLayout
            title={copy.pageTitle}
            description={copy.description}
            onCancel={onCancel}
            onSave={handleSave}
            isLoading={isLoading}
            isValid={isValid}
            cancelLabel={copy.cancel}
            saveLabel={copy.save}
        >
            <div className="w-full flex flex-col justify-start items-start gap-1.5">
                <label htmlFor="trade-confirmation-email" className="text-slate-900 text-sm font-medium leading-5">
                    {copy.emailLabel}
                </label>
                <input
                    id="trade-confirmation-email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                        hasEditedEmail.current = true
                        setEmail(e.target.value)
                    }}
                    placeholder={copy.emailPlaceholder}
                    disabled={isLoading}
                    className="w-full h-11 pl-4 pr-4 bg-white rounded-full border border-gray-300 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-base font-normal leading-6 placeholder:text-dust-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
            </div>
        </AccountSettingsFormLayout>
    )
}
